import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  applyImport, chooseProgramPdf, extractImport, fetchImports, fetchProgramPdf, previewImport, uploadImport,
} from './ProgramAdminApi';
import { Button, Card, CardTitle, Field, Input, Message } from './ui';
import {
  EmptyDay, MiniButton, MiniButtonPrimary, PersonMeta, PersonName, StateTag as StatusTag, Table, TableScroll,
  Td, Th,
} from './programAdminStyles';

// The "Imports & PDF" area (plan §7.7 item 3): everything that comes in as a document and everything
// that goes out as one. An import is a PDF the admin uploads; the AI extraction runs on it once, the
// plan built from that extraction is shown read-only (the per-person/per-entry decisions are a later
// work package, so preview and apply always send an empty decisions object), and applying a complete
// plan writes the program - which is why the parent is told via `onChanged` to reload it. The same
// list holds the PDF that is offered publicly for download (`programPdf`).
//
// Every call goes through ./ProgramAdminApi; a 4xx/5xx answers a plain-text body that is what the
// admin should read.

// The status of an import, coloured like the spot states but with its own palette. An unknown status
// is rendered as-is in the neutral tone (the backend may add one before this screen knows it).
const STATUS_TONE = {
  UPLOADED: { bg: '#f1f5f9', fg: '#475569', border: '#cbd5e1' },
  EXTRACTING: { bg: '#fef3c7', fg: '#92400e', border: '#fcd34d' },
  EXTRACTED: { bg: '#e0f2fe', fg: '#075985', border: '#7dd3fc' },
  APPLIED: { bg: '#dcfce7', fg: '#166534', border: '#86efac' },
  FAILED: { bg: '#fee2e2', fg: '#991b1b', border: '#fecaca' },
};
const NEUTRAL_TONE = { bg: '#f1f5f9', fg: '#475569', border: '#cbd5e1' };

const toneOf = (status) => STATUS_TONE[status] || NEUTRAL_TONE;

// A file size a human reads at a glance: bytes under 1 KB, then KB, then MB.
const formatSize = (bytes) => {
  if (typeof bytes !== 'number' || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// The raw file of one import, served by ProgramImportController (the API module does not wrap it).
const fileUrl = (eventId, id) => `${process.env.REACT_APP_API_URL || ''}/api/cms/events/${eventId}/program/imports/${id}/file`;

const ImportsArea = ({ eventId, onChanged }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [imports, setImports] = useState([]);
  const [programPdf, setProgramPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [file, setFile] = useState(null);
  const [plan, setPlan] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // The import list and the chosen program PDF, loaded together. Dependencies: `eventId` (whose
  // imports these are), `reloadKey` (bumped after every action that changes what the backend holds),
  // `intl` (the error texts) and `navigate` (the 401 redirect, as on the other admin screens).
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    Promise.all([fetchImports(eventId), fetchProgramPdf(eventId)])
      .then(([rows, pdf]) => {
        if (!live) return;
        setImports(rows || []);
        setProgramPdf(pdf || null);
      })
      .catch((err) => {
        if (!live) return;
        if (err.response?.status === 401) { navigate('/admin/login'); return; }
        setError(typeof err.response?.data === 'string' && err.response.data
          ? err.response.data
          : intl.formatMessage({ id: 'programAdmin.imports.loadFailed', defaultMessage: 'Could not load the imports.' }));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [eventId, reloadKey, intl, navigate]);

  // One action at a time: `busy` names the running one, and every failure lands in the same place.
  const run = async (key, task, failedId, failedDef) => {
    setBusy(key);
    setActionError(null);
    try {
      await task();
    } catch (err) {
      if (err.response?.status === 401) { navigate('/admin/login'); return; }
      setActionError(typeof err.response?.data === 'string' && err.response.data
        ? err.response.data
        : intl.formatMessage({ id: failedId, defaultMessage: failedDef }));
    } finally {
      setBusy(null);
    }
  };

  const upload = () => {
    if (!file) {
      setActionError(intl.formatMessage({ id: 'programAdmin.imports.upload.chooseFirst', defaultMessage: 'Choose a PDF file first.' }));
      return;
    }
    run('upload', async () => {
      await uploadImport(eventId, file);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      setReloadKey((k) => k + 1);
    }, 'programAdmin.imports.uploadFailed', 'The upload failed.');
  };

  const extract = (imp) => run(`extract:${imp.id}`, async () => {
    await extractImport(eventId, imp.id);
    setReloadKey((k) => k + 1);
  }, 'programAdmin.imports.extractFailed', 'The extraction failed.');

  const preview = (imp) => run(`preview:${imp.id}`, async () => {
    const built = await previewImport(eventId, imp.id, {});
    setPlan({ ...built, importId: imp.id });
  }, 'programAdmin.imports.previewFailed', 'Could not build the plan.');

  const apply = (imp) => run(`apply:${imp.id}`, async () => {
    await applyImport(eventId, imp.id, {});
    setPlan(null);
    setReloadKey((k) => k + 1);
    onChanged?.();
  }, 'programAdmin.imports.applyFailed', 'Could not apply the plan.');

  const choose = (importId) => run('choose', async () => {
    const chosen = await chooseProgramPdf(eventId, importId);
    setProgramPdf(chosen || null);
    onChanged?.();
  }, 'programAdmin.imports.chooseFailed', 'Could not choose the program PDF.');

  const planImport = plan ? imports.find((i) => i.id === plan.importId) : null;
  const blocking = plan?.blocking || [];
  const warnings = plan?.warnings || [];

  return (
    <div>
      <Card>
        <CardTitle><FormattedMessage id="programAdmin.imports.title" defaultMessage="Imports & PDF" /></CardTitle>
        <p style={{ margin: '0 0 1rem', fontSize: '.85rem', color: '#475569', lineHeight: 1.6 }}>
          <FormattedMessage id="programAdmin.imports.intro"
            defaultMessage="Upload the program as a PDF, let the AI read it, review the plan it built and apply it. The same list holds the PDF that is offered publicly for download." />
        </p>
        <Field>
          <FormattedMessage id="programAdmin.imports.upload.pick" defaultMessage="Program PDF file" />
          <Input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </Field>
        <div style={{ marginTop: '.75rem' }}>
          <Button type="button" onClick={upload} disabled={busy !== null || !file}>
            {busy === 'upload'
              ? <FormattedMessage id="programAdmin.imports.upload.busy" defaultMessage="Uploading…" />
              : <FormattedMessage id="programAdmin.imports.upload.button" defaultMessage="Upload" />}
          </Button>
        </div>
        {actionError && (
          <Message error style={{ marginTop: '1rem', marginBottom: 0 }}>{actionError}</Message>
        )}
      </Card>

      <Card>
        <CardTitle><FormattedMessage id="programAdmin.imports.list.title" defaultMessage="Imports" /></CardTitle>
        {loading && (
          <PersonMeta><FormattedMessage id="programAdmin.imports.loading" defaultMessage="Loading imports…" /></PersonMeta>
        )}
        {!loading && error && (
          <Message error>
            {error}
            <Button type="button" secondary style={{ marginLeft: '.6rem' }} onClick={() => setReloadKey((k) => k + 1)}>
              <FormattedMessage id="programAdmin.imports.retry" defaultMessage="Retry" />
            </Button>
          </Message>
        )}
        {!loading && !error && imports.length === 0 && (
          <EmptyDay><FormattedMessage id="programAdmin.imports.empty" defaultMessage="No imports yet." /></EmptyDay>
        )}
        {imports.length > 0 && (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th><FormattedMessage id="programAdmin.imports.col.file" defaultMessage="File" /></Th>
                  <Th><FormattedMessage id="programAdmin.imports.col.size" defaultMessage="Size" /></Th>
                  <Th><FormattedMessage id="programAdmin.imports.col.status" defaultMessage="Status" /></Th>
                  <Th><FormattedMessage id="programAdmin.imports.col.created" defaultMessage="Uploaded" /></Th>
                  <Th><FormattedMessage id="programAdmin.imports.col.actions" defaultMessage="Actions" /></Th>
                </tr>
              </thead>
              <tbody>
                {imports.map((imp) => {
                  const tone = toneOf(imp.status);
                  const canExtract = imp.status === 'UPLOADED' || imp.status === 'FAILED';
                  const canPreview = imp.status === 'EXTRACTED' || imp.status === 'APPLIED';
                  const shown = plan && plan.importId === imp.id ? plan : null;
                  return (
                    <tr key={imp.id}>
                      <Td><PersonName>{imp.fileName}</PersonName></Td>
                      <Td>{formatSize(imp.fileSize)}</Td>
                      <Td>
                        <StatusTag $bg={tone.bg} $fg={tone.fg} $border={tone.border} data-status={imp.status}>
                          {imp.status}
                        </StatusTag>
                        {imp.programPdf && (
                          <StatusTag $bg="#ede9fe" $fg="#5b21b6" $border="#ddd6fe" style={{ marginLeft: '.3rem' }}>
                            <FormattedMessage id="programAdmin.imports.programPdfTag" defaultMessage="Program PDF" />
                          </StatusTag>
                        )}
                      </Td>
                      <Td>
                        {imp.createdAt
                          ? intl.formatDate(new Date(imp.createdAt), { dateStyle: 'medium', timeStyle: 'short' })
                          : ''}
                      </Td>
                      <Td>
                        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '.3rem', alignItems: 'center' }}>
                          {canExtract && (
                            <MiniButton type="button" disabled={busy !== null} onClick={() => extract(imp)}>
                              <FormattedMessage id="programAdmin.imports.action.extract" defaultMessage="Extract" />
                            </MiniButton>
                          )}
                          {canPreview && (
                            <MiniButton type="button" disabled={busy !== null} onClick={() => preview(imp)}>
                              <FormattedMessage id="programAdmin.imports.action.preview" defaultMessage="Preview plan" />
                            </MiniButton>
                          )}
                          <MiniButtonPrimary type="button" disabled={busy !== null || !shown || shown.complete !== true} onClick={() => apply(imp)}>
                            <FormattedMessage id="programAdmin.imports.action.apply" defaultMessage="Apply" />
                          </MiniButtonPrimary>
                          <MiniButton type="button" disabled={busy !== null} onClick={() => choose(imp.id)}>
                            <FormattedMessage id="programAdmin.imports.action.useAsPdf" defaultMessage="Use as program PDF" />
                          </MiniButton>
                          <a
                            href={fileUrl(eventId, imp.id)}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: '.72rem', fontWeight: 700, color: '#0369a1' }}
                          >
                            <FormattedMessage id="programAdmin.imports.action.download" defaultMessage="Download" />
                          </a>
                        </span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Card>

      <Card>
        <CardTitle><FormattedMessage id="programAdmin.imports.pdf.title" defaultMessage="Program PDF" /></CardTitle>
        {programPdf && programPdf.fileName ? (
          <p style={{ margin: '0 0 .75rem', fontSize: '.88rem', color: '#111D4E', lineHeight: 1.6 }}>
            <FormattedMessage id="programAdmin.imports.pdf.chosen"
              defaultMessage="Offered publicly for download: {fileName}" values={{ fileName: programPdf.fileName }} />
            {' '}
            {programPdf.pdfPublic
              ? <FormattedMessage id="programAdmin.imports.pdf.public" defaultMessage="It is public." />
              : <FormattedMessage id="programAdmin.imports.pdf.private" defaultMessage="It is not public yet." />}
          </p>
        ) : (
          <p style={{ margin: '0 0 .75rem', fontSize: '.88rem', color: '#64748b', lineHeight: 1.6 }}>
            <FormattedMessage id="programAdmin.imports.pdf.noneChosen"
              defaultMessage="No program PDF is chosen yet. Use “Use as program PDF” on an import above." />
          </p>
        )}
        <Button type="button" secondary disabled={busy !== null} onClick={() => choose(null)}>
          <FormattedMessage id="programAdmin.imports.pdf.none" defaultMessage="None" />
        </Button>
      </Card>

      {plan && (
        <Card>
          <CardTitle>
            <FormattedMessage id="programAdmin.imports.plan.title" defaultMessage="Plan for {fileName}"
              values={{ fileName: planImport?.fileName || '' }} />
          </CardTitle>
          <p style={{ margin: '0 0 .4rem', fontSize: '.85rem', color: '#111D4E' }}>
            <FormattedMessage id="programAdmin.imports.plan.state"
              defaultMessage="{complete, select, yes {The plan is complete - applying it is allowed.} other {The plan is incomplete - applying it would be refused.}}"
              values={{ complete: plan.complete ? 'yes' : 'no' }} />
          </p>
          <p style={{ margin: '0 0 .6rem', fontSize: '.85rem', color: '#475569' }}>
            <FormattedMessage id="programAdmin.imports.plan.entries"
              defaultMessage="{count, plural, one {# entry} other {# entries}} in the plan."
              values={{ count: plan.entries?.length || 0 }} />
          </p>
          {blocking.length > 0 && (
            <>
              <h4 style={{ margin: '0 0 .3rem', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.04em', color: '#92400e' }}>
                <FormattedMessage id="programAdmin.imports.plan.blocking" defaultMessage="Missing before it can be applied" />
              </h4>
              <ul style={{ margin: '0 0 .8rem', paddingLeft: '1.1rem', color: '#7f1d1d', fontSize: '.82rem', lineHeight: 1.6 }}>
                {blocking.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </>
          )}
          {warnings.length > 0 && (
            <>
              <h4 style={{ margin: '0 0 .3rem', fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.04em', color: '#92400e' }}>
                <FormattedMessage id="programAdmin.imports.plan.warnings" defaultMessage="Warnings" />
              </h4>
              <ul style={{ margin: '0 0 .8rem', paddingLeft: '1.1rem', color: '#475569', fontSize: '.82rem', lineHeight: 1.6 }}>
                {warnings.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </>
          )}
          <div style={{ marginTop: '.4rem' }}>
            <Button type="button" secondary onClick={() => setPlan(null)}>
              <FormattedMessage id="programAdmin.imports.plan.close" defaultMessage="Close" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ImportsArea;
